/* eslint-disable */

/**
 * Dispatch countdown and Afterpay instalment line for the default product template.
 *
 * Dispatch uses Australia/Melbourne local time. The same-day cutoff is a whole hour
 * (8am by default) on Monday–Friday. Weekends and times after the cutoff roll to the
 * next business day. Public holidays are NOT excluded — a public holiday can still
 * show the same-day message. Confirm holiday closures separately.
 */

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {{ weekdayIndex: number, hour: number, minute: number }}
 */
function melbourneClock(date, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || 'Australia/Melbourne',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });
  const parts = formatter.formatToParts(date);
  const value = (type) => {
    const part = parts.find((item) => item.type === type);
    return part ? part.value : '';
  };
  const weekdayToken = value('weekday').replace('.', '').slice(0, 3);
  const weekdayIndex = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[weekdayToken];
  let hour = parseInt(value('hour'), 10);
  if (hour === 24) hour = 0;
  return {
    weekdayIndex,
    hour,
    minute: parseInt(value('minute'), 10)
  };
}

/**
 * @param {Date} date
 * @param {number} cutoffHour
 * @param {string} timeZone
 * @returns {{ mode: 'same-day', hours: number, minutes: number } | { mode: 'next-day', when: 'Tomorrow' | 'Monday' }}
 */
function getDispatchState(date, cutoffHour, timeZone) {
  const clock = melbourneClock(date, timeZone);
  const cutoff = Number.isFinite(cutoffHour) ? cutoffHour : 8;
  const { weekdayIndex, hour, minute } = clock;

  if (weekdayIndex === undefined || Number.isNaN(hour) || Number.isNaN(minute)) {
    return { mode: 'next-day', when: 'Monday' };
  }

  const isWeekday = weekdayIndex >= 1 && weekdayIndex <= 5;
  if (isWeekday && hour < cutoff) {
    const remaining = (cutoff * 60) - ((hour * 60) + minute);
    return {
      mode: 'same-day',
      hours: Math.floor(remaining / 60),
      minutes: remaining % 60
    };
  }

  const tomorrowIndex = (weekdayIndex + 1) % 7;
  let nextBusinessIndex = tomorrowIndex;
  while (nextBusinessIndex === 0 || nextBusinessIndex === 6) {
    nextBusinessIndex = (nextBusinessIndex + 1) % 7;
  }

  return {
    mode: 'next-day',
    when: nextBusinessIndex === tomorrowIndex ? 'Tomorrow' : 'Monday'
  };
}

if (!customElements.get('dispatch-notice')) {
  class DispatchNotice extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('[data-dispatch-message]');
      this.section = this.closest('.js-product');
      this.cutoffHour = parseInt(this.dataset.cutoffHour || '8', 10);
      this.timeZone = this.dataset.timezone || 'Australia/Melbourne';
      this.onVariantChange = this.handleVariantChange.bind(this);

      if (this.section) {
        this.section.addEventListener('on:variant:change', this.onVariantChange);
      }

      this.render();
      this.timer = window.setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() {
      if (this.section && this.onVariantChange) {
        this.section.removeEventListener('on:variant:change', this.onVariantChange);
      }
      if (this.timer) window.clearInterval(this.timer);
    }

    handleVariantChange(event) {
      const variant = event.detail ? event.detail.variant : null;
      const available = Boolean(variant && variant.available);
      this.hidden = !available;
      this.setAttribute('aria-hidden', available ? 'false' : 'true');
      if (available) this.render();
    }

    render() {
      if (!this.messageEl || this.hidden) return;

      const state = getDispatchState(new Date(), this.cutoffHour, this.timeZone);
      let message = '';

      if (state.mode === 'same-day') {
        message = (this.dataset.sameDay || '')
          .replace('__HOURS__', String(state.hours))
          .replace('__MINUTES__', String(state.minutes));
      } else {
        const whenWord = state.when === 'Tomorrow'
          ? (this.dataset.tomorrow || 'Tomorrow')
          : (this.dataset.monday || 'Monday');
        message = (this.dataset.nextDay || '').replace('__WHEN__', whenWord);
      }

      if (message && this.messageEl.textContent !== message) {
        this.messageEl.textContent = message;
      }
    }
  }

  customElements.define('dispatch-notice', DispatchNotice);
}

if (!customElements.get('afterpay-message')) {
  class AfterpayMessage extends HTMLElement {
    connectedCallback() {
      this.amountEl = this.querySelector('[data-afterpay-amount]');
      this.section = this.closest('.js-product');
      const dataEl = this.querySelector('script[type="application/json"]');
      this.variants = {};

      if (dataEl) {
        try {
          this.variants = JSON.parse(dataEl.textContent);
        } catch (error) {
          this.variants = {};
        }
      }

      this.onVariantChange = (event) => {
        const variant = event.detail ? event.detail.variant : null;
        this.render(variant && variant.id);
      };

      if (this.section) {
        this.section.addEventListener('on:variant:change', this.onVariantChange);
      }
    }

    disconnectedCallback() {
      if (this.section && this.onVariantChange) {
        this.section.removeEventListener('on:variant:change', this.onVariantChange);
      }
    }

    render(variantId) {
      const entry = variantId ? this.variants[String(variantId)] : null;
      const show = Boolean(entry && entry.show && entry.instalment);
      this.hidden = !show;
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show && this.amountEl) {
        this.amountEl.innerHTML = entry.instalment;
      }
    }
  }

  customElements.define('afterpay-message', AfterpayMessage);
}

document.addEventListener('on:variant:change', (event) => {
  const info = event.target && event.target.closest ? event.target.closest('.product-info--merch') : null;
  if (!info) return;

  const badge = info.querySelector('[data-buybox-save]');
  const valueEl = info.querySelector('[data-buybox-save-value]');
  if (!badge || !valueEl) return;

  const variant = event.detail ? event.detail.variant : null;
  const price = variant ? variant.price : 0;
  const compare = variant ? variant.compare_at_price : 0;
  const formatted = event.target && event.target.data && event.target.data.formatted
    ? event.target.data.formatted[String(variant && variant.id)]
    : null;
  const amountWrap = badge.querySelector('[data-buybox-save-amount]');
  const moneyEl = badge.querySelector('[data-buybox-save-money]');

  if (compare && price && compare > price) {
    valueEl.textContent = String(Math.round(((compare - price) * 100) / compare));
    if (moneyEl) {
      const amount = formatted && formatted.saveAmount
        ? String(formatted.saveAmount).replace(/<[^>]*>/g, '').trim()
        : '';
      moneyEl.textContent = amount;
      if (amountWrap) amountWrap.hidden = amount === '';
    }
    badge.hidden = false;
  } else {
    badge.hidden = true;
    if (amountWrap) amountWrap.hidden = true;
  }
});
